import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import { API_URL } from '../../../utils/constants';
import './UnitsFindScreen.css';

function UnitsFindScreen() {
  const [searchCriteria, setSearchCriteria] = useState({
    unitNumber: '',
    address: '',
    unitType: '',
    cleaningZone: '',
    bookingEngine: '',
    active: 'all'
  });
  
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unitTypes, setUnitTypes] = useState([]);
  const [cleaningZones, setCleaningZones] = useState([]);
  const [bookingEngines, setBookingEngines] = useState([]);

  useEffect(() => {
    fetchUnitTypes();
    fetchCleaningZones();
    fetchBookingEngines();
  }, []);

  const fetchUnitTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/unit-types`);
      if (response.ok) {
        const data = await response.json();
        setUnitTypes(data);
      }
    } catch (error) {
      console.error('Error fetching unit types:', error);
    }
  };

  const fetchCleaningZones = async () => {
    try {
      const response = await fetch(`${API_URL}/cleaning-zones`);
      if (response.ok) {
        const data = await response.json();
        setCleaningZones(data);
      }
    } catch (error) {
      console.error('Error fetching cleaning zones:', error);
    }
  };

  const fetchBookingEngines = async () => {
    try {
      const response = await fetch(`${API_URL}/booking-engines`);
      if (response.ok) {
        const data = await response.json();
        setBookingEngines(data);
      }
    } catch (error) {
      console.error('Error fetching booking engines:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Build search query
      const searchTerms = [];
      if (searchCriteria.unitNumber) searchTerms.push(searchCriteria.unitNumber);
      if (searchCriteria.address) searchTerms.push(searchCriteria.address);

      if (searchTerms.length === 0) {
        setError('Please enter at least one search criteria');
        setLoading(false);
        return;
      }

      // Use the search endpoint for better results
      const searchQuery = searchTerms.join(' ');
      const response = await fetch(`${API_URL}/units/search/${encodeURIComponent(searchQuery)}?limit=50`);
      
      if (response.ok) {
        let results = await response.json();
        
        // Apply additional filters
        if (searchCriteria.unitType) {
          results = results.filter(unit => 
            unit.unit_type_name && unit.unit_type_name.toLowerCase().includes(searchCriteria.unitType.toLowerCase())
          );
        }
        
        if (searchCriteria.cleaningZone) {
          results = results.filter(unit => 
            unit.cleaning_zone && unit.cleaning_zone.toLowerCase().includes(searchCriteria.cleaningZone.toLowerCase())
          );
        }
        
        if (searchCriteria.bookingEngine) {
          results = results.filter(unit => 
            unit.booking_engine && unit.booking_engine.toLowerCase().includes(searchCriteria.bookingEngine.toLowerCase())
          );
        }
        
        if (searchCriteria.active !== 'all') {
          const activeFilter = searchCriteria.active === 'true';
          results = results.filter(unit => unit.active === activeFilter);
        }
        
        setSearchResults(results);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error searching units');
      }
    } catch (error) {
      console.error('Error searching units:', error);
      setError('Error searching units. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearSearch = () => {
    setSearchCriteria({
      unitNumber: '',
      address: '',
      unitType: '',
      cleaningZone: '',
      bookingEngine: '',
      active: 'all'
    });
    setSearchResults([]);
    setError('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container fluid className="units-find-screen">
      <Row>
        <Col>
          <h1 className="mb-4">Find Units</h1>
          
          {/* Search Form */}
          <Card className="mb-4">
            <Card.Header>
              <h5>Search Criteria</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="unitNumber"
                        value={searchCriteria.unitNumber}
                        onChange={handleInputChange}
                        placeholder="Enter unit number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type="text"
                        name="address"
                        value={searchCriteria.address}
                        onChange={handleInputChange}
                        placeholder="Enter address"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit Type</Form.Label>
                      <Form.Control
                        as="select"
                        name="unitType"
                        value={searchCriteria.unitType}
                        onChange={handleInputChange}
                      >
                        <option value="">All Types</option>
                        {unitTypes.map(type => (
                          <option key={type.id} value={type.unit_type}>
                            {type.unit_type}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Control
                        as="select"
                        name="active"
                        value={searchCriteria.active}
                        onChange={handleInputChange}
                      >
                        <option value="all">All Statuses</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cleaning Zone</Form.Label>
                      <Form.Control
                        as="select"
                        name="cleaningZone"
                        value={searchCriteria.cleaningZone}
                        onChange={handleInputChange}
                      >
                        <option value="">All Zones</option>
                        {cleaningZones.map(zone => (
                          <option key={zone.id} value={zone.name}>
                            {zone.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Booking Engine</Form.Label>
                      <Form.Control
                        as="select"
                        name="bookingEngine"
                        value={searchCriteria.bookingEngine}
                        onChange={handleInputChange}
                      >
                        <option value="">All Engines</option>
                        {bookingEngines.map(engine => (
                          <option key={engine.id} value={engine.name}>
                            {engine.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <div className="d-flex gap-2">
                      <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Searching...
                          </>
                        ) : (
                          'Search'
                        )}
                      </Button>
                      <Button type="button" variant="secondary" onClick={clearSearch}>
                        Clear
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {/* Error Alert */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Card>
              <Card.Header>
                <h5>Search Results ({searchResults.length} units found)</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Unit #</th>
                      <th>Type</th>
                      <th>Address</th>
                      <th>Zone</th>
                      <th>Engine</th>
                      <th>Status</th>
                      <th>Clean Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(unit => (
                      <tr key={unit.id}>
                        <td>{unit.unit_number}</td>
                        <td>{unit.unit_type_name}</td>
                        <td>{unit.address}</td>
                        <td>{unit.cleaning_zone}</td>
                        <td>{unit.booking_engine}</td>
                        <td>
                          <span className={`badge ${unit.active ? 'bg-success' : 'bg-secondary'}`}>
                            {unit.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${unit.clean_status === 'Clean' ? 'bg-success' : 'bg-warning'}`}>
                            {unit.clean_status}
                          </span>
                        </td>
                        <td>
                          <Button size="sm" variant="outline-primary" className="me-2">
                            View
                          </Button>
                          <Button size="sm" variant="outline-warning" className="me-2">
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* No Results Message */}
          {searchResults.length === 0 && !loading && !error && (
            <Card>
              <Card.Body className="text-center text-muted">
                <p>Enter search criteria and click Search to find units</p>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default UnitsFindScreen;
